/**
 * <s-glossary> — filters the product glossary sheet.
 * Category chips are buttons that invoke `--glossary-filter` on this element
 * (value = group handle, or "all"); the search field filters rows by text.
 * Without JS every group and term stays visible.
 */
class SGlossary extends HTMLElement {
  #ac;
  #filter = 'all';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (e) => {
      if (e.command === '--glossary-open') {
        this.#openAt(e.source instanceof HTMLButtonElement ? e.source.value : '');
        return;
      }
      if (e.command === '--glossary-clear') {
        const field = this.querySelector('[data-glossary-search]');
        if (field) { field.value = ''; field.focus(); }
        this.#update();
        return;
      }
      if (e.command !== '--glossary-filter' || !(e.source instanceof HTMLElement)) return;
      this.#filter = e.source.value || 'all';
      this.#update();
      this.closest('.overflow-y-auto')?.scrollTo({ top: 0 });
    }, opts);

    this.querySelector('[data-glossary-search]')?.addEventListener('input', () => this.#update(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Opens the sheet with every term visible, then scrolls to and highlights one term (spec links). */
  #openAt(handle) {
    const field = this.querySelector('[data-glossary-search]');
    if (field) field.value = '';
    this.#filter = 'all';
    this.#update();

    for (const el of this.querySelectorAll('[data-active]')) el.removeAttribute('data-active');

    const dialog = this.closest('dialog');
    if (dialog && !dialog.open) dialog.showModal();

    const row = handle ? this.querySelector(`#glossary-term-${CSS.escape(handle)}`) : null;
    if (!row) return;
    row.setAttribute('data-active', '');
    requestAnimationFrame(() => row.scrollIntoView({ block: 'start' }));
  }

  #update() {
    const search = this.querySelector('[data-glossary-search]');
    const query = (search?.value || '').trim().toLowerCase();
    const clear = this.querySelector('[data-glossary-clear]');
    if (clear) clear.hidden = !search?.value;

    for (const chip of this.querySelectorAll('[data-glossary-filter]')) {
      chip.setAttribute('aria-pressed', String(chip.value === this.#filter));
    }

    let visible = 0;
    for (const group of this.querySelectorAll('[data-glossary-group]')) {
      const inFilter = this.#filter === 'all' || group.dataset.glossaryGroup === this.#filter;
      let groupVisible = 0;
      for (const row of group.querySelectorAll('[data-glossary-text]')) {
        const match = inFilter && (!query || row.dataset.glossaryText.includes(query));
        row.hidden = !match;
        if (match) groupVisible += 1;
      }
      group.hidden = groupVisible === 0;
      visible += groupVisible;
    }

    const empty = this.querySelector('[data-glossary-empty]');
    if (empty) empty.hidden = visible > 0;
  }
}

if (!customElements.get('s-glossary')) customElements.define('s-glossary', SGlossary);
